import { useEffect, type CSSProperties } from 'react';
import InlineText from '../../InlineEdit/InlineText';
import InlineTextArea from '../../InlineEdit/InlineTextArea';
import {
  DEFAULT_CTA_LABEL,
  DEFAULT_RESTAURANT_ID,
  clampHeight,
  ensureZenchefSdk,
  getBookingPageUrl,
  getDisplay,
  getSurface,
  getZenchefWidgetUrl,
  normalizeRestaurantId,
  openZenchefWidget,
} from './utils';
import './zenchef.scss';

const DEFAULTS = {
  eyebrow: 'Réserver',
  heading: 'Réservez votre table en ligne.',
};

const Widget = ({
  restaurantId,
  styleName,
  height,
}: {
  restaurantId: string;
  styleName: unknown;
  height: number;
}) => {
  const widgetStyle = { '--zenchef-height': `${height}px` } as CSSProperties;

  return (
    <div className="zenchef-widget" style={widgetStyle}>
      <iframe
        src={getZenchefWidgetUrl(restaurantId, getSurface(styleName))}
        title="Réservation en ligne via Zenchef"
        height={height}
        loading="lazy"
      />
    </div>
  );
};

const View = (props: any) => {
  const { data, isEditMode, block, onChangeBlock } = props;

  const update = (field: string, value: string) =>
    onChangeBlock(block, { ...data, [field]: value });

  const eyebrow = data.eyebrow ?? (isEditMode ? '' : DEFAULTS.eyebrow);
  const heading = data.heading ?? (isEditMode ? '' : DEFAULTS.heading);
  const description = data.description ?? '';
  const ctaLabel = data.cta_label || DEFAULT_CTA_LABEL;

  const display = getDisplay(data.display);
  const restaurantId = normalizeRestaurantId(
    data.restaurant_id ?? DEFAULT_RESTAURANT_ID,
  );
  const height = clampHeight(data.height);
  const styleName = data.styles?.style_name;

  const loadSdk = display === 'button' && !isEditMode && Boolean(restaurantId);
  useEffect(() => {
    if (loadSdk) ensureZenchefSdk(restaurantId);
  }, [loadSdk, restaurantId]);

  if (!restaurantId && !isEditMode) return null;

  const onCtaClick = (event: React.MouseEvent) => {
    if (isEditMode || openZenchefWidget()) event.preventDefault();
  };

  const classes = [
    'zenchef',
    `zenchef--${display}`,
    isEditMode ? 'zenchef--edit' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={classes} id="reservation">
      <div className="shell">
        <div className="zenchef-grid">
          <div className="zenchef-head">
            {isEditMode ? (
              <>
                <InlineText
                  value={eyebrow}
                  onChange={(v) => update('eyebrow', v)}
                  renderTag="p"
                  renderClassName="eyebrow"
                  placeholder="Surtitre"
                  ariaLabel="Surtitre"
                />
                <InlineText
                  value={heading}
                  onChange={(v) => update('heading', v)}
                  renderTag="h2"
                  placeholder="Titre"
                  ariaLabel="Titre"
                />
                <InlineTextArea
                  value={description}
                  onChange={(v) => update('description', v)}
                  renderTag="p"
                  renderClassName="zenchef-description"
                  placeholder="Description (facultative)"
                  ariaLabel="Description"
                />
              </>
            ) : (
              <>
                {eyebrow && <p className="eyebrow">{eyebrow}</p>}
                {heading && <h2>{heading}</h2>}
                {description && (
                  <p className="zenchef-description">{description}</p>
                )}
              </>
            )}
          </div>
          <div className="zenchef-side">
            {!restaurantId ? (
              <p className="zenchef-placeholder">
                Indiquez l’identifiant Zenchef du restaurant dans la barre
                latérale pour afficher le module de réservation.
              </p>
            ) : display === 'button' ? (
              <a
                className="btn btn-primary zenchef-cta"
                href={getBookingPageUrl(restaurantId)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onCtaClick}
              >
                {isEditMode ? (
                  <InlineText
                    value={data.cta_label ?? ''}
                    onChange={(v) => update('cta_label', v)}
                    renderTag="span"
                    placeholder={DEFAULT_CTA_LABEL}
                    ariaLabel="Texte du bouton"
                  />
                ) : (
                  ctaLabel
                )}
                <span className="arrow" aria-hidden="true">
                  →
                </span>
              </a>
            ) : (
              <Widget
                restaurantId={restaurantId}
                styleName={styleName}
                height={height}
              />
            )}
            {isEditMode && restaurantId && (
              <p className="zenchef-edit-hint">
                {display === 'button'
                  ? 'Le bouton ouvre le module en surimpression sur le site publié.'
                  : 'Le module est inactif en mode édition.'}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default View;
